import { type FC, useEffect, useState } from 'react'

import CancelIcon from '@/assets/icons/icon-message-cancel.svg'
import { Tooltip } from '@/components/ui/tooltip'
import { classNames } from '@/utils/helpers'

import { useTriggerRerender } from '../../hooks/use-trigger-rerender'
import type { MessageQueue } from '../../types'
import { TimeDifference } from '../components/TimeDifference'
import { MessageQueueIndicator } from './MessageQueueIndicator'

interface Props {
  queue: MessageQueue
  onUnsend: (queueId: number) => void
}

const TWO_MINUTES = 2 * 60 * 1000

export const MessageQueueItem: FC<Props> = ({ queue, onUnsend }) => {
  const { startTimer, stopTimer, timer, inititated } = useTriggerRerender(
    1 * 1000,
    false,
  )

  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (timer && timer > TWO_MINUTES) {
      stopTimer()
      setIsVisible(false)
    }

    if (queue.isDone && !inititated) {
      startTimer()
    }
  }, [inititated, isVisible, queue.isDone, startTimer, stopTimer, timer])

  if (!isVisible) {
    return null
  }

  return (
    <div
      key={queue.id}
      className={classNames(
        'flex items-center gap-1 rounded-md bg-secondary-50 p-2 px-2 dark:bg-secondary-500',
      )}
    >
      <MessageQueueIndicator state={queue.isDone ? 'DONE' : 'IN_PROGRESS'} />
      <span>
        Message sent to{' '}
        {!queue?.isDone
          ? `${queue?.total - queue?.pending}/${queue.total}`
          : `${queue?.total}`}{' '}
        fans <TimeDifference date={queue?.date} />.
      </span>
      {!queue.isCanceled && queue.canUnsend && (
        <Tooltip
          content="Unsend"
          className="ml-auto"
        >
          <CancelIcon
            onClick={() => onUnsend(queue.id)}
            className="hover:text-danger/30 ml-auto h-4 w-4 cursor-pointer text-danger-default"
          />
        </Tooltip>
      )}
    </div>
  )
}
